import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form, Row, Col, Accordion, Nav, Tab, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import EquipmentForm from './EquipmentForm';
import { FullEquipment, Subcategory, QueryParams } from '../interfaces';
// אייקונים
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faCheck,
  faEye,
  faExclamationTriangle,
  faTags,
  faWeightHanging
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import EquipmentDetailModal from './EquipmentDetailModal';
import EquipmentRecommendationsTab from './EquipmentRecommendationsTab';
import DefectReportGeneralModal from './DefectReportGeneralModal';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const groupEquipment = (
    equipment: FullEquipment[], 
    categorySortOrder: {[key: string]: 'asc' | 'desc' | null} = {},
    subcategorySortOrder: {[key: string]: 'asc' | 'desc' | null} = {},
    globalSortOrder: 'asc' | 'desc' | null = null
) => {
    const grouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    
    // קיבוץ הציוד לפי קטגוריות ותת-קטגוריות
    equipment.forEach(item => {
        const category = item.category_name || 'כללי';
        const subcategory = item.subcategory_name || 'כללי';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });

    // מיון פריטים בתוך כל תת-קטגוריה לפי שם (רק אם יש מיון ספציפי לתת-קטגוריה)
    Object.keys(grouped).forEach(category => {
        Object.keys(grouped[category]).forEach(subcategory => {
            const sortOrder = subcategorySortOrder[subcategory];
            if (sortOrder) {
                grouped[category][subcategory].sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    const comparison = nameA.localeCompare(nameB, 'he');
                    return sortOrder === 'desc' ? -comparison : comparison;
                });
            }
        });
    });

    // מיון תתי-קטגוריות לפי שם (רק אם יש מיון ספציפי לקטגוריה)
    Object.keys(grouped).forEach(category => {
        const sortOrder = categorySortOrder[category];
        if (sortOrder) {
            const sortedSubcategories: { [subcategory: string]: FullEquipment[] } = {};
            const subcategoryKeys = Object.keys(grouped[category]).sort((a, b) => {
                const comparison = a.localeCompare(b, 'he');
                return sortOrder === 'desc' ? -comparison : comparison;
            });
            
            subcategoryKeys.forEach(subcategory => {
                sortedSubcategories[subcategory] = grouped[category][subcategory];
            });
            
            grouped[category] = sortedSubcategories;
        }
    });

    // מיון קטגוריות לפי שם (רק אם יש מיון כללי)
    if (globalSortOrder) {
        const sortedGrouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
        const categoryKeys = Object.keys(grouped).sort((a, b) => {
            const comparison = a.localeCompare(b, 'he');
            return globalSortOrder === 'desc' ? -comparison : comparison;
        });
        
        categoryKeys.forEach(category => {
            sortedGrouped[category] = grouped[category];
        });
        
        return sortedGrouped;
    }

    return grouped;
};

const EquipmentList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<FullEquipment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [equipmentToEdit, setEquipmentToEdit] = useState<FullEquipment | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterDefective, setFilterDefective] = useState<string>('');
  const [filterSubcategory, setFilterSubcategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  
  // מצבי מיון חדשים
  const [categorySortOrder, setCategorySortOrder] = useState<{[key: string]: 'asc' | 'desc' | null}>({});
  const [subcategorySortOrder, setSubcategorySortOrder] = useState<{[key: string]: 'asc' | 'desc' | null}>({});
  const [globalSortOrder, setGlobalSortOrder] = useState<'asc' | 'desc' | null>(null);
  
  // מצב לשמירת האקורדיון הפתוח
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  
  
  // <--- תיקון 4: מצב לכמויות הזמינות והעדכון שלהן
  const [availableQuantities, setAvailableQuantities] = useState<Map<number, number>>(new Map());
  const today = moment().format('YYYY-MM-DD');

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetail, setSelectedEquipmentForDetail] = useState<FullEquipment | null>(null);
  const [showDefectReportModal, setShowDefectReportModal] = useState<boolean>(false);
  
  // תיקון 8: מצב לטאב הנוכחי
  const [activeTab, setActiveTab] = useState<string>('equipment');

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await axios.get(buildApiUrl('settings/subcategories/'));
        setSubcategories(response.data);
      } catch (err) {
        // שגיאה בטעינת תת-קטגוריות לפילטר
      }
    };
    fetchSubcategories();
  }, []);

  const fetchEquipment = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: QueryParams = {};
      if (searchQuery) params.search = searchQuery;
      if (filterDefective) params.is_defective = filterDefective === 'true' ? true : false;
      if (filterSubcategory) params.subcategory = parseInt(filterSubcategory);
      if (sortBy) params.ordering = sortBy;

      const response = await axios.get(buildApiUrl('inventory/equipment/'), { params });
      setEquipment(response.data);

      const availableMap = new Map<number, number>();
      response.data.forEach((eq: FullEquipment) => {
        if (eq.id) {
          availableMap.set(eq.id, eq.quantity);
        }
      });
      setAvailableQuantities(availableMap);

    } catch (err) {
      setError("לא ניתן לטעון ציוד. וודא ששרת Django פועל.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchEquipment();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, filterDefective, filterSubcategory, sortBy]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  const handleAddClick = () => { setEquipmentToEdit(null); setShowForm(true); };
  const handleEditClick = (item: FullEquipment) => { setEquipmentToEdit(item); setShowForm(true); };
  const handleFormSuccess = (savedEquipmentName?: string) => { 
    setShowForm(false); 
    fetchEquipment(); 
    if (equipmentToEdit) {
      showMessage('הצלחה', `הציוד '${equipmentToEdit.name}' עודכן בהצלחה!`, 'success'); 
    } else {
      const nameToDisplay = savedEquipmentName?.trim();
      showMessage('הצלחה', nameToDisplay ? `פריט הציוד "${nameToDisplay}" נשמר בהצלחה!` : 'פריט ציוד חדש נוסף למערכת בהצלחה!', 'success'); 
    }
  };
  const handleFormCancel = () => { setShowForm(false); setEquipmentToEdit(null); };

  const handleDeleteClick = (id: number | undefined) => {
    if (id === undefined) {
      showMessage('שגיאה', 'לא ניתן למחוק פריט ללא מזהה.', 'danger');
      return;
    }
    const itemToDelete = equipment.find(eq => eq.id === id);
    setConfirmModalTitle('אישור מחיקה');
    setConfirmModalMessage(`האם אתה בטוח שברצונך למחוק את הציוד '${itemToDelete?.name || 'לא ידוע'}'?`);
    setConfirmAction(() => async () => {
      try {
        await axios.delete(`${buildApiUrl('inventory/equipment/')}${id}/`);
        showMessage('הצלחה', `הציוד '${itemToDelete?.name || 'לא ידוע'}' נמחק מהמערכת בהצלחה!`, 'success');
        fetchEquipment();
      } catch (err) {
        showMessage('שגיאה', 'שגיאה במחיקת הציוד.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleSort = (field: string) => {
    setSortBy(prevSortBy => (prevSortBy === field ? `-${field}` : field));
  };

  // פונקציות מיון חדשות
  const handleCategorySort = (categoryKey: string) => {
    setCategorySortOrder(prev => {
      const current = prev[categoryKey];
      const newOrder = current === null ? 'asc' : current === 'asc' ? 'desc' : null;
      return { ...prev, [categoryKey]: newOrder };
    });
  };

  const handleSubcategorySort = (subcategoryKey: string) => {
    setSubcategorySortOrder(prev => {
      const current = prev[subcategoryKey];
      const newOrder = current === null ? 'asc' : current === 'asc' ? 'desc' : null;
      return { ...prev, [subcategoryKey]: newOrder };
    });
  };

  const handleGlobalSort = () => {
    setGlobalSortOrder(prev => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
  };

  const handleDefectReport = async (_defectData: any) => {
    try {
      fetchEquipment();
    } catch (error) {
      console.error('Error submitting defect report:', error);
      showMessage('שגיאה', 'שגיאה בשליחת דיווח התקלות.', 'danger');
    }
  };

  // <--- תיקון 4: קריאת API לזמינות ציוד להיום
  useEffect(() => {
      const fetchAvailableToday = async () => {
          try {
              const response = await axios.get(`${buildApiUrl('inventory/equipment/available/')}?start_date=${today}&end_date=${today}`);
              const availableMap = new Map<number, number>();
              response.data.forEach((eq: FullEquipment) => {
                  if (eq.id) {
                      availableMap.set(eq.id, eq.quantity);
                  }
              });
              setAvailableQuantities(availableMap);
          } catch (err) {
              console.error("שגיאה בטעינת כמויות זמינות להיום:", err);
          }
      };
      if (!loading) {
          fetchAvailableToday();
      }
  }, [loading]);

  const getSortIndicator = (field: string) => {
    if (sortBy.endsWith(field)) {
      return sortBy.startsWith('-') ? ' 🔽' : ' 🔼';
    }
    return '';
  };

  // פונקציות לקבלת אינדיקטורי מיון חדשים
  const getCategorySortIndicator = (categoryKey: string) => {
    const order = categorySortOrder[categoryKey];
    if (order === 'asc') return ' 🔼';
    if (order === 'desc') return ' 🔽';
    return ' ⇅';
  };

  const getSubcategorySortIndicator = (subcategoryKey: string) => {
    const order = subcategorySortOrder[subcategoryKey];
    if (order === 'asc') return ' 🔼';
    if (order === 'desc') return ' 🔽';
    return ' ⇅';
  };

  const getGlobalSortIndicator = () => {
    if (globalSortOrder === 'asc') return ' 🔼';
    if (globalSortOrder === 'desc') return ' 🔽';
    return ' ⇅';
  };

  const handleRowClick = (item: FullEquipment) => {
    setSelectedEquipmentForDetail(item);
    setShowDetailModal(true);
  };

  
  const groupedEquipment = useMemo(() => 
    groupEquipment(equipment, categorySortOrder, subcategorySortOrder, globalSortOrder), 
    [equipment, categorySortOrder, subcategorySortOrder, globalSortOrder]
  );
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>ניהול ציוד</h2>
        <div>
          <Button variant="info" onClick={() => navigate('/categories')} className="me-2">
            <FontAwesomeIcon icon={faTags} className="me-1" />
            ניהול קטגוריות
          </Button>
          <Button variant="warning" onClick={() => setShowDefectReportModal(true)} className="me-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            דיווח על תקלות
          </Button>
          <Button variant="success" onClick={handleAddClick}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            הוסף ציוד חדש
          </Button>
        </div>
      </div>

      <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'equipment')} className="mb-4">
        <Nav.Item>
          <Nav.Link eventKey="equipment">רשימת ציוד</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="recommendations">המלצות ציוד</Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="equipment" active={activeTab === 'equipment'}>
          <Form className="mb-4 p-3 border rounded bg-light">
            <Row>
              <Col md={3}>
                <Form.Group controlId="searchQuery">
                  <Form.Label>חיפוש</Form.Label>
                  <Form.Control type="text" placeholder="חפש לפי שם, מספר סידורי..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="filterDefective">
                  <Form.Label>סינון תקול</Form.Label>
                  <Form.Select value={filterDefective} onChange={(e) => setFilterDefective(e.target.value)}>
                    <option value="">הכל</option>
                    <option value="true">תקול</option>
                    <option value="false">תקין</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="filterSubcategory">
                  <Form.Label>סינון לפי תת-קטגוריה</Form.Label>
                  <Form.Select value={filterSubcategory} onChange={(e) => setFilterSubcategory(e.target.value)}>
                    <option value="">הכל</option>
                    {subcategories.map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        {subcat.subcategory_name} ({subcat.category_name})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="globalSort">
                  <Form.Label>מיון כללי</Form.Label>
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleGlobalSort}
                    className="w-100"
                  >
                    מיון קטגוריות {getGlobalSortIndicator()}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>

          {loading && <Spinner animation="border" />}
          {error && <Alert variant="danger">{error}</Alert>}

          {!loading && !error && (
            Object.keys(groupedEquipment).length === 0 ? (
              <p>אין ציוד במערכת.</p>
            ) : (
              <Accordion 
                activeKey={openAccordionItems}
                onSelect={(eventKey) => {
                  if (eventKey && typeof eventKey === 'string') {
                    setOpenAccordionItems(prev => 
                      prev.includes(eventKey) 
                        ? prev.filter(key => key !== eventKey)
                        : [...prev, eventKey]
                    );
                  }
                }}
              >
                  {Object.entries(groupedEquipment).map(([category, subcategories]) => (
                      <Accordion.Item eventKey={category} key={category}>
                          <Accordion.Header style={{ cursor: 'pointer' }}>
                            <span 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategorySort(category);
                              }}
                              style={{ display: 'inline-block', cursor: 'pointer' }}
                            >
                              {category} {getCategorySortIndicator(category)}
                            </span>
                          </Accordion.Header>
                          <Accordion.Body>
                              {Object.entries(subcategories).map(([subcategory, items]) => (
                                  <div key={subcategory}>
                                      <h4>
                                        <span 
                                          onClick={() => handleSubcategorySort(subcategory)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          {subcategory} {getSubcategorySortIndicator(subcategory)}
                                        </span>
                                      </h4>
                                      <Table striped bordered hover responsive>
                                          <thead>
                                              <tr>
                                                  <th>
                                                    שם
                                                  </th>
                                                  <th>כמות כוללת</th>
                                                  <th>כמות תקולה</th>
                                                  <th>כמות זמינה</th>
                                                  <th>סטטוס</th>
                                                  <th>פעולות</th>
                                              </tr>
                                          </thead>
                                          <tbody>
                                              {items.map(item => (
                                                  <tr key={item.id} onClick={() => handleRowClick(item)} style={{ cursor: 'pointer', backgroundColor: item.defective_quantity > 0 ? '#ffe6e6' : 'inherit' }}>
                                                                                                             <td>
                                                         {item.defective_quantity > 0 && <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />}
                                                         {item.name}
                                                         {item.is_heavy && <span className="text-danger"> (כבד)</span>}
                                                       </td>
                                                      <td>{item.quantity}</td>
                                                      <td>{item.defective_quantity || 0}</td>
                                                      <td>{item.available_quantity || (availableQuantities.get(item.id!) !== undefined ? availableQuantities.get(item.id!) : 'N/A')}</td>
                                                      <td>
                                                        {item.is_defective ? (
                                                          <Badge bg="danger">תקול</Badge>
                                                        ) : item.defective_quantity > 0 ? (
                                                          <Badge bg="warning">חלקית תקול</Badge>
                                                        ) : (
                                                          <Badge bg="success">תקין</Badge>
                                                        )}
                                                      </td>
                                                      <td>
                                                                                                                 <Button variant="warning" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleEditClick(item); }}>
                                                         <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                         ערוך
                                                       </Button>
                                                       <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id); }} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחוק פריטים' : ''}>
                                                         <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                         מחק
                                                       </Button>
                                                      </td>
                                                  </tr>
                                              ))}
                                          </tbody>
                                      </Table>
                                  </div>
                              ))}
                          </Accordion.Body>
                      </Accordion.Item>
                  ))}
              </Accordion>
            )
          )}
        </Tab.Pane>

        <Tab.Pane eventKey="recommendations" active={activeTab === 'recommendations'}>
          <EquipmentRecommendationsTab />
        </Tab.Pane>

      </Tab.Content>

      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{equipmentToEdit ? 'עריכת ציוד' : 'הוספת ציוד'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EquipmentForm equipmentToEdit={equipmentToEdit} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
        </Modal.Body>
      </Modal>

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
      <EquipmentDetailModal show={showDetailModal} onHide={() => setShowDetailModal(false)} equipment={selectedEquipmentForDetail} allEquipment={equipment} />
      <DefectReportGeneralModal 
        show={showDefectReportModal} 
        onHide={() => setShowDefectReportModal(false)} 
        onDefectReport={handleDefectReport} 
      />
    </div>
  );
};

export default EquipmentList;
